import {useEffect, useState} from 'react';
import {useInterval} from 'react-use';

export type WordStreamerProps = {
  // The text we want to stream, which may be incomplete.
  text: string;

  // Whether or not the input text is complete.
  textComplete: boolean;

  // The delay between emitting each word in milliseconds.
  delayMs: number;

  // Invoked once the input text is complete and we are finished streaming it.
  onFinished: () => void;

  children: (outputText: string) => JSX.Element | null;
};

export const WordStreamer = (props: WordStreamerProps) => {
  const {text, textComplete, delayMs, onFinished, children} = props;

  // If our initial text is already complete, just emit it all at once rather than word-by-word.
  // So if someone reloads the page after streaming was complete we don't start over again.
  const [cursorIndex, setCursorIndex] = useState(
    textComplete ? text.length : 0,
  );

  // Render a word at a time starting at the cursor index.
  useInterval(
    () => {
      let nextCursorIndex = cursorIndex;

      while (nextCursorIndex < text.length) {
        if (text[nextCursorIndex] === ' ' && nextCursorIndex !== cursorIndex) {
          break;
        }
        nextCursorIndex += 1;
      }

      setCursorIndex(nextCursorIndex);
    },
    // Pass null once we are finished to stop the timer.
    cursorIndex < text.length ? delayMs : null,
  );

  // If the input text is complete and we have rendered it all, then we are finished.
  useEffect(() => {
    if (
      (textComplete && cursorIndex === text.length) ||
      cursorIndex > text.length
    ) {
      onFinished();
    }
  }, [textComplete, cursorIndex, text.length, onFinished]);

  return children(text.slice(0, cursorIndex));
};
